import React from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import {
  OpenWith,
  Create,
  Delete,
  Visibility,
  VisibilityOff,
  Add,
  ArrowBack,
  ArrowForward,
  ArrowDownward,
  ArrowUpward,
} from "@mui/icons-material";
import clsx from "clsx";
import {
  removeElement,
  updateConfirmation,
  updateElement,
  updateModalManager,
  updateMultiSelection,
  updateSpawnWalls,
} from "../../../reducers/configurator/configuratorSlice";
import { useDispatch } from "react-redux";
import { useGetDesc } from "../utils/utils";
import { resetLayers } from "../../../reducers/configurator/utils";
import EventModification from "../configuratorForms/EventModification";
import FrameForm from "../configuratorForms/FrameForm";
import ObjectSideManagement from "./ObjectSideManagement";
import ConfiguratorIcons from "../ConfiguratorIcons";
import DoNotTouchIcon from "@mui/icons-material/DoNotTouch";
import PanToolIcon from "@mui/icons-material/PanTool";

const PREFIX = "quickActionsDetails";

const classes = {
  root: `${PREFIX}-root`,
  spawnIcons: `${PREFIX}-spawnIcons`,
  iconButtonStyle: `${PREFIX}-iconButtonStyle`,
  selectedOffset: `${PREFIX}-selectedOffset`,
  marginRight: `${PREFIX}-marginRight`,
};

const QuickActionsDetailsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
    },
    [`& .${classes.spawnIcons}`]: {
      display: "flex",
    },
    [`& .${classes.iconButtonStyle}`]: {
      marginRight: "0.5rem !important",
      padding: 0,
    },
    [`& .${classes.marginRight}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        marginRight: "0",
      },
      marginRight: "0.5rem",
    },
    [`& .${classes.selectedOffset}`]: {
      backgroundColor: theme.palette.primary.main,
      "& > svg": {
        fill: "#303030",
      },
    },
  };
});

const QuickActionsDetails = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const layout = useSelector((state) => state.configurator.layout);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const myTile = useSelector((state) =>
    selectedTile
      ? state.configurator.grid[parentLayout][selectedTile.row][
          selectedTile.column
        ]
      : null
  );
  const myDesc = useGetDesc(
    !myTile ? "" : props?.layer ? myTile[props?.layer] : myTile[layout]
  );

  const dialogRender = {
    custom: {
      renderer: "custom",
      title: t("configurator.3dModalTitle"),
    },
    guest: { renderer: "guest", title: t("configurator.guestTitle") },
    screen: {
      renderer: "screen",
      title: t("configurator.screen"),
    },
    ipad: {
      renderer: "ipad",
      title: t("configurator.screen"),
    },
  };

  const manageSpawnWalls = (modifyTile, direction) => {
    dispatch(
      updateSpawnWalls({
        tile: modifyTile,
        old: myTile[props.layer].offset,
        direction,
      })
    );
  };

  const centerSpawn = (direction) => {
    dispatch(
      updateElement({
        layer: props.layer,
        data: {
          ...myTile[props.layer],
          offset: direction === myTile[props.layer].offset ? null : direction,
        },
        tile: selectedTile,
      })
    );
    manageSpawnWalls(selectedTile, direction);
  };

  // const isEndTileEqualToStartTile = (startTile, endTile) => {
  //   return startTile.row === endTile.row && startTile.column === endTile.column;
  // };

  const actions = {
    normal: [
      <div key={`actions-${props?.layer}`} className={classes.spawnIcons}>
        {myTile && myTile[props?.layer]?.type === "custom" && (
          <IconButton
            color="primary"
            aria-label="element interaction"
            component="span"
            className={clsx(classes.iconButtonStyle, classes.marginRight)}
            onClick={() => {
              dispatch(
                updateElement({
                  layer: props.layer,
                  data: {
                    ...myTile[props.layer],
                    interaction: !myTile[props.layer].interaction,
                  },
                  tile: myTile[props.layer].startTile
                    ? myTile[props.layer].startTile
                    : selectedTile,
                  tileEnd: myTile[props.layer].endTile
                    ? myTile[props.layer].endTile
                    : null,
                })
              );
            }}
          >
            {myTile[props.layer].interaction ? (
              <PanToolIcon fontSize="medium" />
            ) : (
              <DoNotTouchIcon fontSize="medium" />
            )}
          </IconButton>
        )}
        {myTile &&
          (myTile[props?.layer]?.type === "custom" ||
            myTile[props?.layer]?.type === "frame") && (
            <IconButton
              color="primary"
              aria-label="element visibility"
              component="span"
              className={clsx(classes.iconButtonStyle, classes.marginRight)}
              onClick={() => {
                dispatch(
                  updateElement({
                    layer: props.layer,
                    data: {
                      ...myTile[props.layer],
                      visibleRoom: !myTile[props.layer].visibleRoom,
                    },
                    tile: myTile[props.layer].startTile
                      ? myTile[props.layer].startTile
                      : selectedTile,
                    tileEnd: myTile[props.layer].endTile
                      ? myTile[props.layer].endTile
                      : null,
                  })
                );
              }}
            >
              {myTile[props.layer].visibleRoom ? (
                <Visibility fontSize="medium" />
              ) : (
                <VisibilityOff fontSize="medium" />
              )}
            </IconButton>
          )}
        {myTile && myTile[props?.layer]?.type === "spawn" && (
          <div>
            <IconButton
              color="primary"
              aria-label="offset spawn top"
              component="span"
              className={clsx(
                classes.iconButtonStyle,
                classes.marginRight,
                myTile[props?.layer].offset === "top"
                  ? classes.selectedOffset
                  : ""
              )}
              onClick={() => {
                centerSpawn("top");
              }}
            >
              <ArrowUpward fontSize="medium" />
            </IconButton>
            <IconButton
              color="primary"
              aria-label="offset span right"
              component="span"
              className={clsx(
                classes.iconButtonStyle,
                classes.marginRight,
                myTile[props?.layer].offset === "right" &&
                  classes.selectedOffset
              )}
              onClick={() => centerSpawn("right")}
            >
              <ArrowForward fontSize="medium" />
            </IconButton>
            <IconButton
              color="primary"
              aria-label="offset span bottom"
              component="span"
              className={clsx(
                classes.iconButtonStyle,
                classes.marginRight,
                myTile[props?.layer].offset === "bottom" &&
                  classes.selectedOffset
              )}
              onClick={() => centerSpawn("bottom")}
            >
              <ArrowDownward fontSize="medium" />
            </IconButton>
            <IconButton
              color="primary"
              aria-label="offset span left"
              component="span"
              className={clsx(
                classes.iconButtonStyle,
                classes.marginRight,
                myTile[props?.layer].offset === "left" && classes.selectedOffset
              )}
              onClick={() => centerSpawn("left")}
            >
              <ArrowBack fontSize="medium" />
            </IconButton>
          </div>
        )}
        {myTile && myTile[props?.layer]?.type !== "arcdetriomphe" && (
          <IconButton
            color="primary"
            aria-label="move element"
            component="span"
            className={clsx(classes.iconButtonStyle, classes.marginRight)}
            onClick={() => {
              if (
                myTile &&
                myTile[props?.layer].hasOwnProperty("endTile")
                // &&
                // endTileIsDifferentFromStart(
                //   myTile[props?.layer].startTile,
                //   myTile[props?.layer].endTile
                // )
              ) {
                const isScreen =
                  myTile[props.layer].type === "screen" ||
                  myTile[props.layer].type === "ipad" ||
                  (myTile[props.layer].type === "frame" &&
                    myTile[props.layer].size === "custom");
                dispatch(
                  updateModalManager({
                    type: "backdrop",
                    title: t("configurator.choseFacade"),
                    open: true,
                    renderer: (
                      <ObjectSideManagement
                        isMultiUpload
                        moveMultiSize={(newLayer) => {
                          dispatch(
                            updateMultiSelection({
                              data: {
                                ...myTile[props.layer],
                                rotation: newLayer,
                              },
                              move: true,
                              active: true,
                              oldLayer: props.layer,
                              layer: newLayer,
                              x: isScreen ? -1 : 1, // change 1 into varaible when we will have objects on fixed size with more than 1 case
                              y: isScreen ? -1 : 1,
                            })
                          );
                        }}
                      />
                    ),
                  })
                );
              } else {
                const tmp = { ...myTile[props.layer] };
                if (tmp.endTile) delete tmp.endTile;
                dispatch(
                  updateMultiSelection({
                    data: tmp,
                    move: true,
                    active: true,
                    tile: selectedTile,
                    oldLayer: props.layer,
                    layer: props.layer,
                  })
                );
              }
            }}
          >
            <OpenWith fontSize="medium" />
          </IconButton>
        )}
        {myTile &&
          (myTile[props?.layer]?.type === "custom" ||
            myTile[props?.layer]?.type === "guest" ||
            myTile[props?.layer]?.type === "frame" ||
            myTile[props?.layer]?.type === "screen" ||
            myTile[props?.layer]?.type === "ipad") && (
            <IconButton
              color="primary"
              aria-label="modify element"
              component="span"
              className={clsx(classes.iconButtonStyle, classes.marginRight)}
              onClick={() => {
                dispatch(
                  updateModalManager({
                    open: true,
                    type: "dialog",
                    renderer:
                      myTile[props?.layer]?.type === "frame" ? (
                        <FrameForm layer={props?.layer} />
                      ) : (
                        dialogRender[myTile[props.layer].type].renderer
                      ),
                    mode: "onlyInfos",
                    layer: props?.layer || null,
                    title:
                      myTile[props?.layer]?.type === "frame"
                        ? `Facade ${props?.layer}`
                        : dialogRender[myTile[props.layer].type].title,
                    orientation: props.layer,
                  })
                );
              }}
            >
              <Create fontSize="medium" />
            </IconButton>
          )}
        {myTile && myTile[props?.layer]?.type === "custom" && (
          <IconButton
            color="primary"
            aria-label="modify element"
            component="span"
            className={clsx(classes.iconButtonStyle, classes.marginRight)}
            onClick={() => {
              dispatch(
                updateModalManager({
                  open: true,
                  type: "dialog",
                  renderer: "custom",
                  mode: "onlyModel",
                  disableGutters: true,
                  title: dialogRender[myTile[props.layer].type].title,
                  orientation: props.layer,
                })
              );
            }}
          >
            <ConfiguratorIcons
              orientation={{ type: myTile[props?.layer]?.type }}
              isForDialog
              side="neutralPosition"
            />
          </IconButton>
        )}
      </div>,
    ],
    event: [
      <div key={`actions-${layout}`}>
        <IconButton
          color="primary"
          aria-label="move element"
          component="span"
          className={clsx(classes.iconButtonStyle, classes.marginRight)}
          onClick={() =>
            dispatch(
              updateMultiSelection({
                data: myTile[layout],
                move: true,
                active: true,
                tile: selectedTile,
                oldLayer: layout,
                layer: layout,
              })
            )
          }
        >
          <OpenWith fontSize="medium" />
        </IconButton>
        <IconButton
          color="primary"
          aria-label="modify element"
          component="span"
          className={clsx(classes.iconButtonStyle, classes.marginRight)}
          onClick={() =>
            dispatch(
              updateModalManager({
                open: true,
                type: "backdrop",
                renderer: <EventModification element={myTile[layout]} />,
                title: t("configurator.eventOptionsModalTitle"),
              })
            )
          }
        >
          <Create fontSize="medium" />
        </IconButton>
      </div>,
    ],
    theme: null,
  };

  return (
    <QuickActionsDetailsContainer sx={{ width: "100%" }}>
      {myTile &&
        !myTile[props?.layer]?.type &&
        props.layer !== "middle" &&
        layout === "normal" && (
          <IconButton
            color="primary"
            aria-label="add frame"
            component="span"
            className={clsx(classes.iconButtonStyle, classes.marginRight)}
            onClick={() => {
              dispatch(
                updateModalManager({
                  open: true,
                  type: "dialog",
                  renderer: <FrameForm layer={props?.layer} />,
                  title: `Facade ${props.layer}`,
                  orientation: props.layer,
                })
              );
            }}
          >
            <Add fontSize="medium" />
          </IconButton>
        )}
      {!myTile ||
      (layout !== "normal" && !myTile[layout]?.type) ||
      (layout === "normal" && !myTile[props.layer].type) ? null : (
        <div className={classes.root}>
          {actions[layout]}
          {layout === "normal" &&
          myTile[props.layer].type === "spawn" ? null : (
            <IconButton
              color="primary"
              aria-label="delete element"
              component="span"
              className={classes.iconButtonStyle}
              onClick={() => {
                const tmp = {
                  layer: props.layer ? props.layer : layout,
                  tile: selectedTile,
                };
                if (
                  (layout === "normal" &&
                    myTile[props.layer].hasOwnProperty("endTile")) ||
                  (layout !== "normal" &&
                    myTile[layout].hasOwnProperty("endTile"))
                ) {
                  tmp.tileEnd =
                    layout === "normal"
                      ? myTile[props.layer].endTile
                      : myTile[layout].endTile;
                  tmp.tile =
                    layout === "normal"
                      ? myTile[props.layer].startTile
                      : myTile[layout].startTile;
                }
                dispatch(
                  updateConfirmation({
                    open: true,
                    agree: () => dispatch(removeElement(tmp)),
                    setOpen: () =>
                      dispatch(updateConfirmation(resetLayers.confirmation)),
                    element: myDesc,
                  })
                );
              }}
            >
              <Delete fontSize="medium" />
            </IconButton>
          )}
        </div>
      )}
    </QuickActionsDetailsContainer>
  );
};

export default QuickActionsDetails;
